import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'

// Evidence-based presets (RSNA 3D Printing SIG, published literature)
const PRESETS = [
  { id: 'bone', label: 'Bone (Ortho)', thresholdMin: 226, thresholdMax: null, smoothing: 25, decimation: 0.5, icon: '🦴', desc: 'RSNA standard' },
  { id: 'craniofacial', label: 'Craniofacial', thresholdMin: 150, thresholdMax: null, smoothing: 25, decimation: 0.5, icon: '💀', desc: 'Thin cranial bone' },
  { id: 'cortical', label: 'Cortical Only', thresholdMin: 500, thresholdMax: null, smoothing: 20, decimation: 0.5, icon: '🦴', desc: 'Dense bone only' },
  { id: 'teeth', label: 'Teeth / Dense', thresholdMin: 1500, thresholdMax: null, smoothing: 15, decimation: 0.4, icon: '🦷', desc: 'Enamel + dentin' },
  { id: 'soft_tissue', label: 'Soft Tissue', thresholdMin: -86, thresholdMax: 350, smoothing: 30, decimation: 0.7, icon: '🫁', desc: 'Organs, muscle' },
  { id: 'skin', label: 'Skin Surface', thresholdMin: -200, thresholdMax: null, smoothing: 35, decimation: 0.8, icon: '👤', desc: 'External surface' },
  { id: 'custom', label: 'Custom', thresholdMin: 226, thresholdMax: null, smoothing: 25, decimation: 0.5, icon: '⚙️', desc: 'Manual settings' },
]

// Grade color classes
const GRADE_COLORS = {
  Excellent: 'text-green-400',
  Good: 'text-blue-400',
  Fair: 'text-yellow-400',
  Poor: 'text-red-400',
}

// Mini sparkline bar chart for HU histogram
function HuHistogram({ data }) {
  if (!data?.counts) return null
  const max = Math.max(...data.counts)
  if (max === 0) return null
  return (
    <div className="flex items-end gap-px h-8">
      {data.counts.map((count, i) => (
        <div
          key={i}
          className="flex-1 bg-brand-500/60 rounded-t-sm min-w-[2px]"
          style={{ height: `${(count / max) * 100}%` }}
          title={`${data.bin_edges[i]} to ${data.bin_edges[i + 1]} HU: ${count}`}
        />
      ))}
    </div>
  )
}

export default function ReconstructPanel({ caseId, seriesId, onClose, headerQuality }) {
  const [preset, setPreset] = useState('bone')
  const [thresholdMin, setThresholdMin] = useState(226)
  const [thresholdMax, setThresholdMax] = useState(null)
  const [smoothing, setSmoothing] = useState(25)
  const [decimation, setDecimation] = useState(0.5)
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Job state
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [polling, setPolling] = useState(false)
  const pollRef = useRef(null)

  // Apply preset values (threshold, smoothing, decimation)
  const applyPreset = useCallback((presetId) => {
    setPreset(presetId)
    const p = PRESETS.find((pr) => pr.id === presetId)
    if (p && presetId !== 'custom') {
      setThresholdMin(p.thresholdMin)
      setThresholdMax(p.thresholdMax)
      setSmoothing(p.smoothing)
      setDecimation(p.decimation)
    }
  }, [])

  // Start reconstruction
  const startReconstruction = useCallback(async () => {
    try {
      const res = await api.post(`/reconstruct/${caseId}/start`, {
        seriesId,
        preset,
        thresholdMin,
        thresholdMax,
        smoothingIterations: smoothing,
        decimateRatio: decimation,
      })
      setJobId(res.data.jobId)
      setPolling(true)
      setJobStatus({
        status: 'downloading',
        progress: 0,
        phase: 'Starting...',
      })
    } catch (err) {
      setJobStatus({
        status: 'failed',
        error: err.response?.data?.error || err.message,
      })
    }
  }, [caseId, seriesId, preset, thresholdMin, thresholdMax, smoothing, decimation])

  // Poll for status
  const pollFailCount = useRef(0)
  useEffect(() => {
    if (!polling || !jobId) return
    pollFailCount.current = 0

    const poll = async () => {
      try {
        const res = await api.get(`/reconstruct/${caseId}/status/${jobId}`)
        pollFailCount.current = 0
        setJobStatus(res.data)

        if (res.data.status === 'complete' || res.data.status === 'failed') {
          setPolling(false)
        }
      } catch (err) {
        pollFailCount.current++
        // If job not found for 5+ consecutive polls (15s), the server likely restarted
        // and lost the in-memory job. Check if a model was created in the DB instead.
        if (pollFailCount.current >= 5) {
          setPolling(false)
          setJobStatus({
            status: 'failed',
            error: 'Lost connection to reconstruction job. The server may have restarted. Please check the case in the admin dashboard — if a model appears, the reconstruction succeeded.',
          })
        }
      }
    }

    poll() // immediate first poll
    pollRef.current = setInterval(poll, 3000)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [polling, jobId, caseId])

  const isRunning = jobStatus && !['complete', 'failed'].includes(jobStatus.status)

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-gray-800 border-l border-gray-700 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
        <h2 className="text-white font-semibold text-lg">3D Reconstruct</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          title="Close panel"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {/* Pre-reconstruction quality summary from header analysis */}
        {headerQuality && !jobStatus && (
          <div className="rounded-lg bg-gray-900 border border-gray-700 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-300">Scan Quality</h3>
              <span className={`text-sm font-semibold ${GRADE_COLORS[headerQuality.printabilityGrade] || 'text-gray-400'}`}>
                {headerQuality.printabilityGrade} ({headerQuality.printabilityScore}/6)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Series Type</span>
              <span className="text-gray-300">{headerQuality.seriesType}</span>
              <span className="text-gray-500">Modality</span>
              <span className="text-gray-300">{headerQuality.modality}</span>
              {headerQuality.kernel && (<>
                <span className="text-gray-500">Kernel</span>
                <span className="text-gray-300">{headerQuality.kernel} ({headerQuality.kernelType})</span>
              </>)}
              {headerQuality.sliceThickness && (<>
                <span className="text-gray-500">Slice Thickness</span>
                <span className="text-gray-300">{headerQuality.sliceThickness} mm</span>
              </>)}
              {headerQuality.pixelSpacing && (<>
                <span className="text-gray-500">Pixel Spacing</span>
                <span className="text-gray-300">{headerQuality.pixelSpacing.map(v => v.toFixed(2)).join(' x ')} mm</span>
              </>)}
              <span className="text-gray-500">Slices</span>
              <span className="text-gray-300">{headerQuality.sliceCount}</span>
              {headerQuality.contrast && (<>
                <span className="text-gray-500">Contrast</span>
                <span className="text-orange-400">Yes</span>
              </>)}
              {headerQuality.gantryTilt !== 0 && Math.abs(headerQuality.gantryTilt) >= 1 && (<>
                <span className="text-gray-500">Gantry Tilt</span>
                <span className="text-yellow-400">{headerQuality.gantryTilt.toFixed(1)}° (auto-corrected)</span>
              </>)}
            </div>
            {headerQuality.warnings.length > 0 && (
              <div className="pt-1 space-y-0.5">
                {headerQuality.warnings.map((w, i) => (
                  <p key={i} className="text-[10px] text-yellow-400/80 leading-tight">⚠ {w}</p>
                ))}
              </div>
            )}
            {headerQuality.recommendations.length > 0 && (
              <div className="pt-1 space-y-0.5">
                {headerQuality.recommendations.map((r, i) => (
                  <p key={i} className="text-[10px] text-blue-400/70 leading-tight">💡 {r}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preset selector */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Preset</label>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id)}
                disabled={isRunning}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                  preset === p.id
                    ? 'bg-brand-600 text-white ring-2 ring-brand-400'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } disabled:opacity-50`}
              >
                <span className="mr-1.5">{p.icon}</span>
                {p.label}
                <span className="block text-[10px] opacity-60 mt-0.5">{p.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Threshold controls */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Threshold (Hounsfield Units)
          </label>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>Min: {thresholdMin} HU</span>
                <span className="text-gray-500">-1024 → 3071</span>
              </div>
              <input
                type="range"
                min={-1024}
                max={3071}
                value={thresholdMin}
                onChange={(e) => {
                  setThresholdMin(Number(e.target.value))
                  setPreset('custom')
                }}
                disabled={isRunning}
                className="w-full accent-brand-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="bandThreshold"
                checked={thresholdMax !== null}
                onChange={(e) => {
                  setThresholdMax(e.target.checked ? Math.max(thresholdMin + 50, 500) : null)
                  setPreset('custom')
                }}
                disabled={isRunning}
                className="accent-brand-500"
              />
              <label htmlFor="bandThreshold" className="text-sm text-gray-400">
                Band threshold (min–max range)
              </label>
            </div>
            {thresholdMax !== null && (
              <div>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Max: {thresholdMax} HU</span>
                </div>
                <input
                  type="range"
                  min={thresholdMin}
                  max={3071}
                  value={thresholdMax}
                  onChange={(e) => {
                    setThresholdMax(Number(e.target.value))
                    setPreset('custom')
                  }}
                  disabled={isRunning}
                  className="w-full accent-brand-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Advanced options */}
        <div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-gray-400 hover:text-gray-300 transition-colors flex items-center gap-1"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Advanced options
          </button>

          {showAdvanced && (
            <div className="mt-3 space-y-3 pl-1">
              <div>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Smoothing: {smoothing} iterations</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={50}
                  value={smoothing}
                  onChange={(e) => setSmoothing(Number(e.target.value))}
                  disabled={isRunning}
                  className="w-full accent-brand-500"
                />
              </div>
              <div>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <span>Decimation: {Math.round(decimation * 100)}% reduction</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={90}
                  value={decimation * 100}
                  onChange={(e) => setDecimation(Number(e.target.value) / 100)}
                  disabled={isRunning}
                  className="w-full accent-brand-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Job status / Progress */}
        {jobStatus && (
          <div className="rounded-lg bg-gray-900 border border-gray-700 p-4 space-y-3">
            {/* Progress bar */}
            {isRunning && (
              <>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-brand-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${jobStatus.progress || 0}%` }}
                  />
                </div>
                <p className="text-sm text-gray-300">{jobStatus.phase}</p>
                {jobStatus.elapsed > 0 && (
                  <p className="text-xs text-gray-500">{jobStatus.elapsed}s elapsed</p>
                )}
              </>
            )}

            {/* Complete */}
            {jobStatus.status === 'complete' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-400">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Model ready</span>
                </div>
                <p className="text-sm text-gray-400">{jobStatus.glbFilename}</p>

                {/* Post-reconstruction volume analysis report */}
                {jobStatus.volumeAnalysis && (() => {
                  const va = jobStatus.volumeAnalysis
                  return (
                    <div className="rounded-lg bg-gray-800 border border-gray-600 p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">Volume Analysis</h4>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {va.noise_hu !== null && (<>
                          <span className="text-gray-500">Noise</span>
                          <span className={va.noise_grade === 'Low' ? 'text-green-400' : va.noise_grade === 'Moderate' ? 'text-yellow-400' : 'text-red-400'}>
                            {va.noise_hu} HU ({va.noise_grade})
                          </span>
                        </>)}
                        <span className="text-gray-500">Bone Volume</span>
                        <span className="text-gray-300">{va.bone_volume_percent}%</span>
                        <span className="text-gray-500">Metal</span>
                        <span className={va.metal_detected ? 'text-orange-400' : 'text-green-400'}>
                          {va.metal_detected ? `Yes (slices ${va.metal_slice_range?.[0]}-${va.metal_slice_range?.[1]})` : 'None'}
                        </span>
                        {va.suggested_threshold_hu && (<>
                          <span className="text-gray-500">Suggested Threshold</span>
                          <span className="text-brand-400">
                            {va.suggested_threshold_hu} HU
                            {va.threshold_confidence && (
                              <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                                va.threshold_confidence === 'high' ? 'bg-green-900/50 text-green-400' :
                                va.threshold_confidence === 'medium' ? 'bg-yellow-900/50 text-yellow-400' :
                                'bg-red-900/50 text-red-400'
                              }`}>
                                {va.threshold_confidence}
                              </span>
                            )}
                          </span>
                        </>)}
                        {va.threshold_methods && va.threshold_methods.valley != null && va.threshold_methods.otsu != null && (<>
                          <span className="text-gray-500">Methods</span>
                          <span className="text-gray-400 text-[10px]">
                            Valley: {va.threshold_methods.valley} HU / Otsu: {va.threshold_methods.otsu} HU
                          </span>
                        </>)}
                      </div>
                      {va.bone_warning && (
                        <p className="text-[10px] text-yellow-400/80">⚠ {va.bone_warning}</p>
                      )}
                      {va.metal_note && (
                        <p className="text-[10px] text-orange-400/80">⚠ {va.metal_note}</p>
                      )}
                      {/* HU Histogram sparkline */}
                      {va.hu_histogram && (
                        <div>
                          <p className="text-[10px] text-gray-500 mb-1">HU Distribution</p>
                          <HuHistogram data={va.hu_histogram} />
                          <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
                            <span>-1024</span>
                            <span>0</span>
                            <span>1000</span>
                            <span>3071</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                <a
                  href={jobStatus.viewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium transition-colors"
                >
                  Open in 3D Viewer
                </a>
                <button
                  onClick={() => {
                    setJobId(null)
                    setJobStatus(null)
                  }}
                  className="w-full text-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
                >
                  Run another reconstruction
                </button>
              </div>
            )}

            {/* Failed */}
            {jobStatus.status === 'failed' && (
              <div className="space-y-2">
                <p className="text-red-400 font-medium">Reconstruction failed</p>
                <p className="text-sm text-gray-400">{jobStatus.error}</p>
                <button
                  onClick={() => {
                    setJobId(null)
                    setJobStatus(null)
                  }}
                  className="w-full text-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer — Generate button */}
      {(!jobStatus || jobStatus.status === 'failed') && (
        <div className="px-5 py-4 border-t border-gray-700">
          <button
            onClick={startReconstruction}
            disabled={isRunning}
            className="w-full px-4 py-3 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors"
          >
            Generate 3D Model
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            This may take 2–5 minutes depending on scan size
          </p>
        </div>
      )}
    </div>
  )
}
